<template>
  <div
    class="builder-container"
    :style="containerStyles"
    :class="{ 'selected': isSelected, 'drag-over': isDragOver }"
    draggable="true"
    @dragstart="handleDragStart"
    @dragend="handleDragEnd"
    @dragover="handleDragOver"
    @dragleave="handleDragLeave"
    @drop="handleDrop"
    @click.stop="handleSelect"
  >
    <div class="container-toolbar">
      <div class="toolbar-left">
        <span class="drag-handle" title="Drag to reorder">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M2 4h12v1H2V4zm0 3.5h12v1H2v-1zm0 3.5h12v1H2v-1z"/>
          </svg>
        </span>
        <span class="badge bg-secondary">Container</span>
      </div>
      <div class="toolbar-right">
        <button type="button" class="icon-btn" title="Edit Container" @click.stop="handleEdit">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
            <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5L13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175l-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z"/>
          </svg>
        </button>
        <button type="button" class="icon-btn" title="Add Column" @click.stop="handleAddColumn">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
          </svg>
        </button>
        <button type="button" class="icon-btn" title="Copy Container" @click.stop="handleCopy">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
            <path d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H6z"/>
            <path d="M2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1h1v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1v1H2z"/>
          </svg>
        </button>
        <button type="button" class="icon-btn icon-btn-danger" title="Delete Container" @click.stop="handleDelete">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
            <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
            <path fill-rule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4L4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
          </svg>
        </button>
      </div>
    </div>

    <div class="row g-2" v-if="container.columns.length > 0">
      <BuilderColumn
        v-for="(column, index) in container.columns"
        :key="column.uid"
        :column="column"
        :column-index="index"
        :container-uid="container.uid"
        :selected-uid="selectedUid"
        @select="$emit('select', $event)"
        @edit="$emit('edit', $event)"
        @update="handleColumnUpdate"
        @delete="handleDeleteColumn"
        @copy="$emit('copyColumn', $event)"
        @save="$emit('saveColumn', $event)"
        @reorder-column="handleReorderColumn"
        @move-element="handleMoveElement"
      />
    </div>

    <div v-else class="empty-container">
      <p class="text-muted">No columns. Click the + button to add one.</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import type { Container, Column, Element } from '../../types/form-builder';
import { applyInlineStyles } from '../../utils/form-helpers';
import BuilderColumn from './BuilderColumn.vue';

interface Props {
  container: Container;
  containerIndex: number;
  selectedUid?: string;
}

interface Emits {
  (e: 'select', uid: string): void;
  (e: 'edit', uid: string): void;
  (e: 'update', container: Container): void;
  (e: 'delete', uid: string): void;
  (e: 'copy', container: Container): void;
  (e: 'copyColumn', column: Column): void;
  (e: 'saveColumn', column: Column): void;
  (e: 'reorder', fromIndex: number, toIndex: number): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const isDragOver = ref(false);

const isSelected = computed(() => props.selectedUid === props.container.uid);

const containerStyles = computed(() => applyInlineStyles(props.container.design));

function handleSelect() {
  emit('select', props.container.uid);
}

function handleEdit() {
  emit('edit', props.container.uid);
}

function handleCopy() {
  emit('copy', props.container);
}

function handleAddColumn() {
  const newColumn: Column = {
    uid: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    type: 'column',
    general: {
      id: `column-${Date.now()}`,
      name: 'Column',
    },
    design: {
      padding: '10px',
    },
    logic: [],
    extras: {},
    width: 12,
    elements: [],
  };

  const updatedContainer = {
    ...props.container,
    columns: [...props.container.columns, newColumn],
  };

  emit('update', updatedContainer);
}

function handleColumnUpdate(updatedColumn: Column) {
  const updatedContainer = {
    ...props.container,
    columns: props.container.columns.map((col) =>
      col.uid === updatedColumn.uid ? updatedColumn : col
    ),
  };

  emit('update', updatedContainer);
}

function handleDeleteColumn(uid: string) {
  const updatedContainer = {
    ...props.container,
    columns: props.container.columns.filter((col) => col.uid !== uid),
  };

  emit('update', updatedContainer);
}

function handleReorderColumn(fromIndex: number, toIndex: number) {
  const newColumns = [...props.container.columns];
  const [moved] = newColumns.splice(fromIndex, 1);
  newColumns.splice(toIndex, 0, moved);

  const updatedContainer = {
    ...props.container,
    columns: newColumns,
  };

  emit('update', updatedContainer);
}

function handleMoveElement(data: { element: Element; fromColumnUid: string; toColumnUid: string; toIndex: number }) {
  const newColumns = props.container.columns.map(col => {
    if (col.uid === data.fromColumnUid) {
      return {
        ...col,
        elements: col.elements.filter(el => el.uid !== data.element.uid)
      };
    }
    if (col.uid === data.toColumnUid) {
      const newElements = [...col.elements];
      newElements.splice(data.toIndex, 0, data.element);
      return {
        ...col,
        elements: newElements
      };
    }
    return col;
  });

  const updatedContainer = {
    ...props.container,
    columns: newColumns,
  };

  emit('update', updatedContainer);
}

function handleDelete() {
  emit('delete', props.container.uid);
}

function handleDragStart(e: DragEvent) {
  if (e.dataTransfer) {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/container', JSON.stringify({
      type: 'container',
      index: props.containerIndex
    }));
  }
}

function handleDragEnd() {
  isDragOver.value = false;
}

function handleDragOver(e: DragEvent) {
  if (e.dataTransfer?.types.includes('application/container')) {
    e.preventDefault();
    isDragOver.value = true;
  }
}

function handleDragLeave() {
  isDragOver.value = false;
}

function handleDrop(e: DragEvent) {
  e.preventDefault();
  isDragOver.value = false;

  const containerData = e.dataTransfer?.getData('application/container');
  if (containerData) {
    const { index: fromIndex } = JSON.parse(containerData);
    if (fromIndex !== props.containerIndex) {
      emit('reorder', fromIndex, props.containerIndex);
    }
  }
}
</script>

<style scoped>
.builder-container {
  border: 2px dashed #6c757d;
  padding: 15px;
  margin: 10px 0;
  border-radius: 8px;
  position: relative;
  min-height: 100px;
  transition: all 0.2s;
}

.builder-container:hover {
  border-color: #0d6efd;
}

.builder-container.selected {
  border-color: #0d6efd;
  border-style: solid;
  background-color: rgba(13, 110, 253, 0.05);
}

.builder-container.drag-over {
  border-color: #198754;
  background-color: rgba(25, 135, 84, 0.1);
}

.container-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e9ecef;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.toolbar-right {
  display: flex;
  gap: 4px;
}

.drag-handle {
  cursor: grab;
  padding: 4px;
  color: #6c757d;
  display: flex;
  align-items: center;
}

.drag-handle:hover {
  color: #0d6efd;
}

.icon-btn {
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid #dee2e6;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #495057;
  transition: all 0.15s;
}

.icon-btn:hover {
  background-color: #e9ecef;
  border-color: #0d6efd;
  color: #0d6efd;
}

.icon-btn-danger:hover {
  background-color: #f8d7da;
  border-color: #dc3545;
  color: #dc3545;
}

.empty-container {
  padding: 30px;
  text-align: center;
  background-color: #f8f9fa;
  border-radius: 4px;
}
</style>
